import React, { useRef, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Route, Routes, useLocation } from "react-router";

import {
  ArrowLeftIcon,
  BellIcon,
  ContactIcon,
  LockIcon,
  PlugIcon,
  SaveIcon,
  SettingsIcon,
} from "lucide-react";

import { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { computerUp } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import { Divider } from "simplydo/react/ui/divider";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H2 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";

import api from "api";
import Prompt from "utils/Prompt";
import { type UserMe, useCurrentUser, userCache } from "utils/currentUser";
import util from "utils/utils";

import { SideNavLink } from "components/lib/SideNav";
import { Sticky } from "components/lib/UI";

import type { ProfileUpdate } from "../types";
import AccountSettings from "./Account";
import ApiSettings from "./ApiSettings";
import NotificationsSettings from "./Notifications";
import ProfileSettings from "./Profile";
import SecuritySettings from "./Security";
const MobileOnlyDivider = styled.div`
  border-bottom: solid grey 1px;
  min-width: 100%;
  min-height: 20px;
  margin-bottom: 5px;

  ${computerUp} {
    display: none;
  }
`;

const UserSettings = () => {
  const user = useCurrentUser();
  const [unsaved, setUnsaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // Kept out of the user cache so a background /me refetch cannot discard unsaved edits
  const [profileDraft, setProfileDraft] = useState<Record<string, any>>({});
  const profile = { ...user?.profile, ...profileDraft } as UserMe["profile"];
  const contextRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const location = useLocation();
  const path = location.pathname;

  const updateProfile = (data: ProfileUpdate) => {
    if (!user) return;
    setUnsaved(true);

    const validFields = [
      "github",
      "linkedin",
      "instagram",
      "twitter",
      "facebook",
    ];
    const processedData: Record<string, any> = {};
    for (const [key, value] of Object.entries(data)) {
      if (validFields.includes(key) && typeof value === "string") {
        if (util.validateUrl(value)) {
          const urlPath = util.getUrlPath(value);
          if (!urlPath) {
            // pass
          } else {
            const splitPath = urlPath.split("/").filter((part) => part !== "");
            processedData[key] = splitPath[splitPath.length - 1] as string;
          }
        } else {
          const splitValue = value
            .split("/")
            .filter((part: string) => part !== "");
          if (splitValue.length > 1) {
            processedData[key] = splitValue[splitValue.length - 1] as string;
          }
        }
      } else {
        processedData[key] = value;
      }
    }

    setProfileDraft((prev) => ({ ...prev, ...processedData }));
  };

  const toggleTag = (tag: OpenAPI.Schemas["Tag"], checked: boolean) => {
    if (!user) return;
    const { ownerTags = [] } = user;

    if (!checked) {
      api.tags.untagSingle(
        "users",
        tag._id,
        user._id,
        (newTag) => {
          const updatedTags = Object.assign([], ownerTags);
          updatedTags.splice(
            ownerTags.findIndex((ft) => ft._id === newTag._id),
            1,
          );
          userCache.updateTags(updatedTags);
        },
        (err) => toast.error(err.message),
      );
    } else {
      api.tags.tagSingle(
        "users",
        tag._id,
        user._id,
        (newTag) => {
          const updatedTags = Object.assign([], ownerTags);
          updatedTags.push(newTag);
          userCache.updateTags(updatedTags);
        },
        (err) => toast.error(err.message),
      );
    }
  };

  const saveProfile = () => {
    if (!user) return;
    setSaving(true);
    api.users.update(
      user._id,
      { profile },
      (user) => {
        toast.success(t("generic.typeUpdated", { type: "Profile" }));
        setSaving(false);
        setUnsaved(false);
        setProfileDraft({});
        userCache.updateProfile(user.profile);
        userCache.addEngagement("savedProfile");
        // Ensures a user always sees their own profile updated in all queries
        api.invalidateQueries();
      },
      (err) => {
        toast.error(err.message);
        setSaving(false);
      },
    );
  };

  return (
    <Container style={{ marginTop: "2rem", marginBottom: 20 }}>
      <Helmet title={"Settings"} />
      <Prompt
        when={
          Boolean(location.pathname.match(/^\/preferences\/profile$/)) &&
          unsaved
        }
        message={"Your profile changes are unsaved. Really leave this page?"}
      />
      <div>
        <ButtonLink variant="outline" to={`/users/${user?._id ?? ""}`}>
          <Icon icon={ArrowLeftIcon} />
          {t("users.settings.viewProfile")}
        </ButtonLink>
      </div>
      <Divider clearing hidden />

      <div ref={contextRef}>
        <Grid mobile={1} columns={4}>
          <GridColumn span={4} computer={1}>
            <Sticky top={60}>
              <H2 style={{ marginTop: 0 }}>{t("generic.settings")}</H2>
              <SideNavLink
                active={path === "/preferences"}
                to="/preferences"
                iconLeft={SettingsIcon}
                content={t("users.settings.sections.account")}
              />
              <SideNavLink
                active={path === "/preferences/profile"}
                to="../profile"
                iconLeft={ContactIcon}
                content={t("users.settings.sections.profile")}
              />
              <SideNavLink
                active={
                  path === "/preferences/security" ||
                  path === "/preferences/password"
                }
                to="../security"
                iconLeft={LockIcon}
                content={t("users.settings.sections.security")}
              />
              <SideNavLink
                active={path === "/preferences/notifications"}
                to="../notifications"
                iconLeft={BellIcon}
                content={t("users.settings.sections.notifications")}
              />
              <SideNavLink
                active={path === "/preferences/connections"}
                to="../connections"
                iconLeft={PlugIcon}
                content={t("users.settings.sections.api")}
              />
              <MobileOnlyDivider />
              {path === "/preferences/profile" && (
                <React.Fragment>
                  <Divider hidden />
                  {unsaved && (
                    <Button
                      style={{ marginBottom: "5px" }}
                      loading={saving}
                      fluid
                      onClick={saveProfile}
                      color="primary"
                    >
                      <Icon icon={SaveIcon} />
                      {t("generic.saveChanges")}
                    </Button>
                  )}
                </React.Fragment>
              )}
            </Sticky>
          </GridColumn>
          <GridColumn span={4} computer={3}>
            <Routes>
              <Route path="/" element={<AccountSettings />} />
              <Route
                path="profile"
                element={
                  <ProfileSettings
                    profile={profile}
                    saveProfile={saveProfile}
                    toggleTag={toggleTag}
                    updateProfile={updateProfile}
                    saving={saving}
                  />
                }
              />
              <Route path="security" element={<SecuritySettings />} />
              <Route path="password" element={<SecuritySettings />} />
              <Route path="email" element={<SecuritySettings />} />
              <Route path="communication" element={<AccountSettings />} />
              <Route path="notifications" element={<NotificationsSettings />} />
              <Route path="connections" element={<ApiSettings />} />
            </Routes>
          </GridColumn>
        </Grid>
      </div>
    </Container>
  );
};

export default UserSettings;
